import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { gameAPI } from '../../services/api';
import { unwrapArray } from '../../utils/unwrapResponse';
import { Button } from '../ui/Button';
import { queryClient } from '../../lib/queryClient';
import { queryKeys } from '../../hooks/queries/queryKeys';

interface ActivePop {
  id: number;
  taskId: number;
  taskPublicId: string;
  taskTitle?: string;
  triggeredAt: string;
  deadlineAt: string;
  status: 'pending' | 'completed' | 'partial' | 'expired' | 'cancelled';
  completedByMe: boolean;
  totalAssignees: number;
  completedCount: number;
}

interface ActivePopBannerProps {
  taskId?: string; // If provided, only shows pops for this task
  onComplete?: () => void; // Callback after completing a pop
  showTaskTitle?: boolean; // Show task title (for global banner)
}

const ActivePopBanner: React.FC<ActivePopBannerProps> = ({
  taskId,
  onComplete,
  showTaskTitle = true,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  // Update time every second for countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Only log the first consecutive failure to avoid console spam from
  // polling; reset on the next success. Mirrors the pre-migration
  // errorCountRef's "log once" behavior exactly.
  const hasLoggedErrorRef = useRef(false);

  const activePopsQueryKey = taskId
    ? queryKeys.activePops.forTask(taskId)
    : queryKeys.activePops.mine();

  const activePopsQuery = useQuery<ActivePop[]>({
    queryKey: activePopsQueryKey,
    queryFn: async (): Promise<ActivePop[]> => {
      try {
        // Get active pops for a specific task, or all pending pops for the
        // current user when no taskId is provided (global banner).
        const response = taskId
          ? await gameAPI.getActivePops(taskId)
          : await gameAPI.getMyPendingPops();
        hasLoggedErrorRef.current = false;
        return unwrapArray<ActivePop>(response.data);
      } catch (err: unknown) {
        // Silently handle expected failures (feature not enabled, no org
        // context, 403/404) — only log on first failure to avoid console
        // spam from polling.
        if (!hasLoggedErrorRef.current) {
          console.debug('Active pops unavailable:', err instanceof Error ? err.message : String(err));
          hasLoggedErrorRef.current = true;
        }
        throw err;
      }
    },
    staleTime: 30_000,
    refetchInterval: (query) => (query.state.fetchFailureCount >= 3 ? false : 30_000),
    // Pre-migration code was a single try/catch with no retry wrapper —
    // opting out explicitly rather than inheriting the global QueryClient
    // default (retry: 2), which would otherwise double up with the
    // consecutive-failure circuit breaker above.
    retry: false,
  });

  const activePops: ActivePop[] = activePopsQuery.data ?? [];
  const loading = activePopsQuery.isLoading;

  const completeMutation = useMutation({
    mutationFn: (pop: ActivePop) => gameAPI.completePop(pop.taskPublicId, pop.id),
    onSuccess: (_data, pop) => {
      queryClient.setQueryData<ActivePop[]>(
        activePopsQueryKey,
        (old: ActivePop[] | undefined) => old?.filter((p: ActivePop) => p.id !== pop.id) ?? old
      );
      onComplete?.();
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to complete pop');
    },
  });

  const completingId = completeMutation.isPending ? completeMutation.variables?.id ?? null : null;

  // Handle completing a pop
  const handleComplete = (pop: ActivePop) => {
    setError(null);
    completeMutation.mutate(pop);
  };

  // Calculate time remaining
  const getTimeRemaining = (deadlineAt: string): { minutes: number; seconds: number; expired: boolean } => {
    const deadline = new Date(deadlineAt).getTime();
    const remaining = deadline - now;

    if (remaining <= 0) {
      return { minutes: 0, seconds: 0, expired: true };
    }

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return { minutes, seconds, expired: false };
  };

  // Format time remaining display
  const formatTimeRemaining = (deadlineAt: string): string => {
    const { minutes, seconds, expired } = getTimeRemaining(deadlineAt);

    if (expired) return 'Expired';
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Get urgency level for styling
  const getUrgencyClass = (deadlineAt: string): string => {
    const { minutes, expired } = getTimeRemaining(deadlineAt);

    if (expired) return 'pop-urgency-expired';
    if (minutes < 1) return 'pop-urgency-critical';
    if (minutes < 2) return 'pop-urgency-high';
    return 'pop-urgency-normal';
  };

  // Filter out completed pops and only show ones user needs to respond to
  const pendingPops = activePops.filter(p => !p.completedByMe && p.status === 'pending');

  if (loading) {
    return null; // Don't show loading state for banner
  }

  if (pendingPops.length === 0) {
    return null; // No active pops to show
  }

  return (
    <div className="flex flex-col gap-2 mb-4">
      {pendingPops.map((pop) => {
        const { expired } = getTimeRemaining(pop.deadlineAt);
        const urgencyClass = getUrgencyClass(pop.deadlineAt);
        const isCompleting = completingId === pop.id;

        return (
          <div key={pop.id} className={`pop-banner ${urgencyClass}`}>
            <div className="text-2xl shrink-0">⏰</div>

            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] text-text-primary flex items-center gap-2">
                Pop!
                {showTaskTitle && <span className="font-normal text-text-secondary overflow-hidden text-ellipsis whitespace-nowrap">{pop.taskTitle}</span>}
              </div>

              <div className="flex items-center gap-3 mt-1 text-[13px]">
                {!expired ? (
                  <span className="text-[var(--theme-primary)] font-medium tabular-nums">
                    {formatTimeRemaining(pop.deadlineAt)} remaining
                  </span>
                ) : (
                  <span className="text-danger font-medium">Time expired</span>
                )}

                {pop.totalAssignees > 1 && (
                  <span className="text-text-tertiary">
                    {pop.completedCount}/{pop.totalAssignees} completed
                  </span>
                )}
              </div>
            </div>

            <div className="shrink-0">
              {!expired ? (
                <Button
                  className="pop-complete-btn"
                  onClick={() => handleComplete(pop)}
                  disabled={isCompleting}
                >
                  {isCompleting ? 'Confirming...' : "I'm here!"}
                </Button>
              ) : (
                <span className="text-text-tertiary text-[13px] italic">Missed</span>
              )}
            </div>
          </div>
        );
      })}

      {error && (
        <div
          className="py-2 px-3 bg-danger/10 border border-danger/30 rounded text-danger text-[13px]"
          onClick={activePopsQuery.failureCount >= 3 ? () => {
            hasLoggedErrorRef.current = false;
            setError(null);
            activePopsQuery.refetch();
          } : undefined}
          style={activePopsQuery.failureCount >= 3 ? { cursor: 'pointer' } : undefined}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default ActivePopBanner;
